import { IMPORT_TARGETS, importTargetLabel, type ImportTarget } from '@church/domain'
import {
  hasLevel,
  ladderAccess,
  type UploadedImport,
  useCommitImport,
  useCustomFields,
  useDryRunImport,
  useImport,
  useSetImportMapping,
  useUploadImport,
} from '@church/data'
import { faArrowRotateLeft, faDownload, faFileImport, faListCheck, faUsers } from '@church/ui-icons'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import {
  Button,
  Card,
  ErrorNote,
  Field,
  Icon,
  PageHeader,
  Select,
  Spinner,
} from '../components/ui.tsx'
import { useBreadcrumbs } from '../lib/breadcrumbs.ts'

/**
 * The import wizard: choose a file, say what the columns mean, see what would
 * happen, then let it run.
 *
 * The dry run is not skippable and the server enforces that — a mapping that
 * looked right and wasn't is cheap to fix before ten thousand rows and
 * expensive after.
 */
export const Route = createFileRoute('/_authed/_shell/people/import')({
  beforeLoad: ({ context }) => {
    if (!hasLevel(ladderAccess(context.me), 'people', 'administrator', { orgWide: true })) {
      throw redirect({ to: '/people' })
    }
  },
  component: ImportScreen,
})

function ImportScreen() {
  const [uploaded, setUploaded] = useState<UploadedImport | null>(null)
  useBreadcrumbs([{ label: 'Import' }])

  return (
    <div className="max-w-4xl space-y-4">
      <PageHeader title="Import people" />
      {uploaded ? (
        <MappingStep uploaded={uploaded} onStartOver={() => setUploaded(null)} />
      ) : (
        <UploadStep onUploaded={setUploaded} />
      )}
    </div>
  )
}

function UploadStep({ onUploaded }: { onUploaded: (result: UploadedImport) => void }) {
  const upload = useUploadImport()
  const [filename, setFilename] = useState<string | null>(null)

  return (
    <Card className="space-y-3 p-4 sm:p-6">
      <Field label="CSV file">
        <input
          type="file"
          accept=".csv,text/csv"
          className="block w-full text-sm text-content-muted file:mr-3 file:rounded-lg file:border-0 file:bg-brand-soft file:px-4 file:py-2 file:text-sm file:font-medium file:text-content-brand"
          onChange={(event) => {
            const file = event.target.files?.[0]
            if (!file) return
            setFilename(file.name)
            // Read here rather than posting multipart: the API takes text, and
            // the client already parses CSV for its own preview.
            void file.text().then((csv) => {
              upload.mutate({ filename: file.name, csv }, { onSuccess: onUploaded })
            })
          }}
        />
      </Field>
      {filename && upload.isPending && <Spinner label={`Reading ${filename}…`} />}
      <p className="text-xs text-content-subtle">
        The first row must be a header. Nothing is written until you have seen the dry run.
      </p>
      <ErrorNote error={upload.error} />
    </Card>
  )
}

function MappingStep({
  uploaded,
  onStartOver,
}: {
  uploaded: UploadedImport
  onStartOver: () => void
}) {
  const { data: job } = useImport(uploaded.id)
  const { data: customFields } = useCustomFields()
  const setMapping = useSetImportMapping(uploaded.id)
  const dryRun = useDryRunImport(uploaded.id)
  const commit = useCommitImport(uploaded.id)
  const navigate = useNavigate()

  const [mapping, setLocalMapping] = useState<Record<string, string | null>>(uploaded.mapping)
  const [mode, setMode] = useState<'create-only' | 'update-existing'>('create-only')
  const [dateFormat, setDateFormat] = useState<'us' | 'iso'>('iso')

  const current = job ?? uploaded
  const status = current.status
  const targets: { value: string; label: string }[] = [
    { value: '', label: '— ignore —' },
    ...IMPORT_TARGETS.map((target) => ({ value: target, label: importTargetLabel(target) })),
    ...(customFields ?? []).map((field) => ({
      value: `custom:${field.id}`,
      label: `${field.label} (custom)`,
    })),
  ]

  const save = () =>
    setMapping.mutate({ mapping, mode, dateFormat }, { onSuccess: () => dryRun.mutate() })

  const mapped = Object.values(mapping).filter(Boolean)
  const ready = mapped.includes('firstName') && mapped.includes('lastName')

  return (
    <div className="space-y-4">
      <Card className="space-y-4 p-4 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-content-default">
            {uploaded.filename} · {uploaded.totalRows} rows
          </h2>
          <Button
            variant="ghost"
            icon={faArrowRotateLeft}
            className="text-xs"
            onClick={onStartOver}
          >
            Different file
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-2xl text-sm">
            <thead>
              <tr className="border-b border-border-subtle text-left text-xs uppercase tracking-wide text-content-subtle">
                <th className="py-2 pr-4 font-medium">Column</th>
                <th className="py-2 pr-4 font-medium">First rows</th>
                <th className="py-2 font-medium">Imports as</th>
              </tr>
            </thead>
            <tbody>
              {(uploaded.headers ?? []).map((header, index) => (
                <tr key={header} className="border-b border-border-subtle align-top">
                  <td className="py-2 pr-4 font-medium text-content-default">{header}</td>
                  <td className="py-2 pr-4 text-content-muted">
                    {(uploaded.sample ?? [])
                      .map((row) => row[index])
                      .filter((value) => value && value !== '')
                      .slice(0, 3)
                      .join(' · ') || <span className="text-content-subtle">empty</span>}
                  </td>
                  <td className="py-2">
                    <Select
                      aria-label={`What ${header} means`}
                      className="max-w-56"
                      value={mapping[header] ?? ''}
                      onChange={(event) =>
                        setLocalMapping((current) => ({
                          ...current,
                          [header]: (event.target.value || null) as ImportTarget | null,
                        }))
                      }
                    >
                      {targets.map((target) => (
                        <option key={target.value} value={target.value}>
                          {target.label}
                        </option>
                      ))}
                    </Select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            label="People already in the directory"
            description="Matched on email, then phone — never on name."
          >
            <Select value={mode} onChange={(event) => setMode(event.target.value as typeof mode)}>
              <option value="create-only">Skip them</option>
              <option value="update-existing">Update them with this file</option>
            </Select>
          </Field>
          <Field
            label="Dates in this file"
            description="Ambiguous dates are rejected, not guessed."
          >
            <Select
              value={dateFormat}
              onChange={(event) => setDateFormat(event.target.value as typeof dateFormat)}
            >
              <option value="iso">2026-03-04 (year first)</option>
              <option value="us">03/04/2026 (month first)</option>
            </Select>
          </Field>
        </div>

        {!ready && (
          <p className="text-xs text-danger-solid">
            Map a column to First name and one to Last name before continuing.
          </p>
        )}
        <ErrorNote error={setMapping.error ?? dryRun.error} />

        <Button
          icon={faListCheck}
          disabled={!ready || setMapping.isPending || dryRun.isPending}
          onClick={save}
        >
          {dryRun.isPending ? 'Checking every row…' : 'Check the file'}
        </Button>
      </Card>

      {(status === 'validated' || status === 'running' || status === 'completed') && (
        <Card className="space-y-3 p-4 sm:p-6">
          <h2 className="text-sm font-semibold text-content-default">
            {status === 'completed' ? 'Imported' : 'What will happen'}
          </h2>

          <div className="flex flex-wrap gap-6 text-sm">
            <Stat label="Rows" value={current.totalRows} />
            {status === 'completed' ? (
              <>
                <Stat label="Created" value={current.createdCount} />
                <Stat label="Updated" value={current.updatedCount} />
                <Stat label="Skipped" value={current.skippedCount} />
              </>
            ) : (
              <>
                <Stat label="Rows with problems" value={current.failedCount} />
                <Stat label="Already in the directory" value={current.skippedCount} />
              </>
            )}
          </div>

          {current.issueCount > 0 && (
            <div className="space-y-2">
              <p className="text-xs text-content-muted">
                {current.issueCount} problem{current.issueCount === 1 ? '' : 's'} — rows with a
                problem are not imported.
              </p>
              <div className="max-h-64 overflow-auto rounded-lg border border-border-subtle">
                <table className="w-full text-sm">
                  <thead className="bg-surface-heading text-left text-xs uppercase text-content-muted">
                    <tr>
                      <th className="px-3 py-2 font-medium">Row</th>
                      <th className="px-3 py-2 font-medium">Column</th>
                      <th className="px-3 py-2 font-medium">Problem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {current.issues.map((issue, index) => (
                      <tr key={`${issue.row}-${index}`} className="border-t border-border-subtle">
                        <td className="px-3 py-1.5 text-content-muted">{issue.row}</td>
                        <td className="px-3 py-1.5 text-content-muted">{issue.column}</td>
                        <td className="px-3 py-1.5 text-content-default">{issue.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <a
                href={`/api/v1/imports/${uploaded.id}/errors.csv`}
                className="inline-flex items-center gap-1.5 text-xs text-content-brand hover:underline"
              >
                <Icon icon={faDownload} className="size-3" />
                Full report (CSV)
              </a>
            </div>
          )}

          <ErrorNote error={commit.error ?? (current.error ? new Error(current.error) : null)} />

          {status === 'validated' && (
            <Button icon={faFileImport} disabled={commit.isPending} onClick={() => commit.mutate()}>
              {commit.isPending
                ? 'Starting…'
                : `Import ${current.totalRows - current.failedCount} people`}
            </Button>
          )}
          {status === 'running' && <Spinner label="Importing… this keeps running if you leave." />}
          {status === 'completed' && (
            <Button icon={faUsers} onClick={() => void navigate({ to: '/people' })}>
              See the directory
            </Button>
          )}
        </Card>
      )}
    </div>
  )
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <p className="text-2xl font-bold tabular-nums text-content-default">{value}</p>
      <p className="text-xs text-content-muted">{label}</p>
    </div>
  )
}
